import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Clock, ExternalLink, Download, ClipboardCheck, ChevronDown, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { api } from '@/lib/api';
import { demandaDoCliente } from '@/lib/distribuicao';
import { useAuth } from '@/lib/auth';
import { usePortalSid } from './PortalLayout';
import type { Demanda, EntregaEscopo, FaseEscopo } from '@/lib/types';
import { FASES_ESCOPO } from '@/lib/types';

export function EntregasPortal() {
  const { user } = useAuth();
  const portalSid = usePortalSid();
  const sid = portalSid ?? user?.solicitanteId ?? '';
  const [tab, setTab] = useState<'demandas' | 'escopo'>('demandas');
  const demandasQ = useQuery({ queryKey: ['portal', 'demandas'], queryFn: () => api.demandas.list().then(r => r.data) });
  const contratosQ = useQuery({ queryKey: ['portal', 'contratos'], queryFn: () => api.contratos.list().then(r => r.data) });
  const escopoQ = useQuery({ queryKey: ['portal', 'escopo'], queryFn: () => api.escopoEntregas.list().then(r => r.data) });

  const demandas: Demanda[] = useMemo(() =>
    (demandasQ.data ?? []).filter(d => demandaDoCliente(d, sid)),
    [demandasQ.data, sid]);
  const escopo: EntregaEscopo[] = escopoQ.data ?? [];

  const entregues = demandas.filter(d => d.status === 'Publicada');
  const pendentes = demandas.filter(d => d.status !== 'Publicada' && d.status !== 'Cancelada' && d.status !== 'Arquivada');
  const pct = demandas.length ? Math.round(100 * entregues.length / demandas.length) : 0;

  const meuContrato = (contratosQ.data ?? []).find(c => c.solicitanteId === sid && c.status === 'Vigente');

  // Escopo grouped by evento
  const escopoByEvento = useMemo(() => {
    const map = new Map<string, EntregaEscopo[]>();
    for (const e of escopo) {
      const key = e.eventoSubtipo || 'Geral';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    }
    return map;
  }, [escopo]);

  const escopoEntregues = escopo.filter(e => e.status === 'Entregue').length;
  const escopoPct = escopo.length ? Math.round(100 * escopoEntregues / escopo.length) : 0;

  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-2xl font-bold text-text">Meu plano</h1>
        <p className="text-sm text-text-2">
          {meuContrato?.identificador ?? 'Contrato vigente'} · {demandas.length} entregas previstas
        </p>
      </header>

      {/* Tabs */}
      {escopo.length > 0 && (
        <div className="flex gap-1 bg-panel-2/60 p-1 rounded-lg w-fit">
          <button onClick={() => setTab('demandas')}
            className={clsx('px-3 py-1.5 text-xs font-semibold rounded-md transition-colors',
              tab === 'demandas' ? 'bg-panel shadow text-text' : 'text-text-2 hover:text-text')}>
            Produções ({demandas.length})
          </button>
          <button onClick={() => setTab('escopo')}
            className={clsx('px-3 py-1.5 text-xs font-semibold rounded-md transition-colors',
              tab === 'escopo' ? 'bg-panel shadow text-text' : 'text-text-2 hover:text-text')}>
            Escopo contratual ({escopo.length})
          </button>
        </div>
      )}

      {tab === 'demandas' ? (
        <>
          {/* Barra de progresso */}
          <div className="bg-panel border border-border rounded-xl p-4">
            <h3 className="text-sm font-bold text-text mb-1">Execução do contrato</h3>
            <p className="text-[12px] text-text-2 mb-3">
              {entregues.length} de {demandas.length} entregas concluídas. {pendentes.length} ainda por vir.
            </p>
            <div className="h-2.5 bg-panel-2 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-green to-emerald-500 transition-all"
                style={{ width: `${pct}%` }} />
            </div>
            <div className="flex justify-between text-[11px] mt-1.5">
              <span><strong className="text-text">{entregues.length}</strong> entregues</span>
              <span className="text-text-2">{pct}% executado</span>
              <span><strong className="text-text">{pendentes.length}</strong> a entregar</span>
            </div>
          </div>

          {/* Duas colunas: entregue × a entregar */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-panel border border-border rounded-xl p-4">
              <h3 className="text-sm font-bold text-text uppercase tracking-wider mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green" />
                O que já foi entregue
                <span className="ml-auto text-xs bg-green/15 text-green px-2 py-0.5 rounded-full">{entregues.length}</span>
              </h3>
              {entregues.length === 0 ? (
                <p className="text-xs text-muted text-center py-6">Nenhuma entrega publicada ainda.</p>
              ) : (
                <div className="space-y-1">
                  {entregues.map(d => (
                    <DemandaRow key={d.id} d={d} done />
                  ))}
                </div>
              )}
            </div>

            <div className="bg-panel border border-border rounded-xl p-4">
              <h3 className="text-sm font-bold text-text uppercase tracking-wider mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-orange" />
                O que ainda vai ser entregue
                <span className="ml-auto text-xs bg-orange/15 text-orange px-2 py-0.5 rounded-full">{pendentes.length}</span>
              </h3>
              {pendentes.length === 0 ? (
                <p className="text-xs text-muted text-center py-6">Tudo entregue!</p>
              ) : (
                <div className="space-y-1">
                  {pendentes.map(d => (
                    <DemandaRow key={d.id} d={d} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Escopo progress */}
          <div className="bg-panel border border-border rounded-xl p-4">
            <h3 className="text-sm font-bold text-text mb-1">Escopo contratual</h3>
            <p className="text-[12px] text-text-2 mb-3">
              {escopoEntregues} de {escopo.length} itens entregues. Acompanhe o que está incluso na sua parceria.
            </p>
            <div className="h-2.5 bg-panel-2 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all"
                style={{ width: `${escopoPct}%` }} />
            </div>
            <div className="flex justify-between text-[11px] mt-1.5">
              <span><strong className="text-text">{escopoEntregues}</strong> entregues</span>
              <span className="text-text-2">{escopoPct}% concluído</span>
              <span><strong className="text-text">{escopo.length - escopoEntregues}</strong> restantes</span>
            </div>
          </div>

          {/* Escopo by evento */}
          {[...escopoByEvento.entries()].map(([evento, items]) => (
            <EscopoEventoCard key={evento} evento={evento} items={items} />
          ))}
        </>
      )}
    </div>
  );
}

function DemandaRow({ d, done }: { d: Demanda; done?: boolean }) {
  const content = (
    <div className="flex items-center gap-2.5 py-2 px-2 rounded-md hover:bg-panel-2/40 transition-colors">
      <div className={clsx(
        'w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0',
        done ? 'bg-green text-white' : 'bg-gray-200 text-gray-500',
      )}>
        {done ? '✓' : '○'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] font-semibold text-text truncate">
          {d.titulo}
          {done && d.linkFinal && <ExternalLink className="w-3 h-3 inline ml-1 text-blue-light opacity-70" />}
        </div>
        {d.pauta && <div className="text-[10.5px] text-text-2 truncate">{d.pauta}</div>}
      </div>
      <div className="text-[10.5px] text-text-2 shrink-0">
        {done && d.dataPublicacao ? new Date(d.dataPublicacao).toLocaleDateString('pt-BR') :
         d.prazoSla ? new Date(d.prazoSla).toLocaleDateString('pt-BR') : '—'}
      </div>
    </div>
  );

  if (done && d.linkFinal) {
    return <a href={d.linkFinal} target="_blank" rel="noreferrer" className="block">{content}</a>;
  }
  return content;
}

void Download;

const STATUS_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  'Pendente': { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Pendente' },
  'Em produção': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Em produção' },
  'Aguardando aprovação': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Aguardando aprovação' },
  'Entregue': { bg: 'bg-green-100', text: 'text-green-700', label: 'Entregue' },
  'Revisão solicitada': { bg: 'bg-red-100', text: 'text-red-700', label: 'Revisão solicitada' },
};

function EscopoEventoCard({ evento, items }: { evento: string; items: EntregaEscopo[] }) {
  const [open, setOpen] = useState(true);
  const entregues = items.filter(e => e.status === 'Entregue').length;

  return (
    <div className="bg-panel border border-border rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full px-4 py-3 flex items-center gap-2 hover:bg-panel-2/30 transition-colors text-left">
        {open ? <ChevronDown className="w-4 h-4 text-text-2" /> : <ChevronRight className="w-4 h-4 text-text-2" />}
        <ClipboardCheck className="w-4 h-4 text-blue-light" />
        <span className="text-sm font-bold text-text flex-1">{evento}</span>
        <span className="text-[11px] text-text-2">{entregues}/{items.length} entregues</span>
      </button>
      {open && (
        <div className="px-4 pb-4">
          {FASES_ESCOPO.map(fase => {
            const faseItems = items.filter(e => e.fase === fase);
            if (faseItems.length === 0) return null;
            const faseEntregues = faseItems.filter(e => e.status === 'Entregue').length;
            return (
              <div key={fase} className="mt-3 first:mt-0">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] font-bold text-text-2 uppercase tracking-wider">{fase}</span>
                  <span className="text-[10px] text-text-2">{faseEntregues}/{faseItems.length}</span>
                </div>
                <div className="space-y-1">
                  {faseItems.map(e => {
                    const badge = STATUS_BADGE[e.status] ?? STATUS_BADGE['Pendente'];
                    return (
                      <div key={e.id} className="flex items-center gap-2.5 py-1.5 px-2 rounded-md hover:bg-panel-2/40 transition-colors">
                        <div className={clsx(
                          'w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0',
                          e.status === 'Entregue' ? 'bg-green text-white' : 'bg-gray-200 text-gray-500',
                        )}>
                          {e.status === 'Entregue' ? '✓' : '○'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[12.5px] font-semibold text-text truncate">{e.entrega}</div>
                          {e.quantidade > 1 && <span className="text-[10px] text-text-2">×{e.quantidade}</span>}
                        </div>
                        <span className={clsx('text-[10px] font-medium px-2 py-0.5 rounded-full', badge.bg, badge.text)}>
                          {badge.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
